import { LOCATION_CHANGE } from 'react-router-redux'
import mergeWith from 'lodash.mergewith'
import uniq from 'lodash.uniq'
import uniqBy from 'lodash.uniqby'

import { LOGIN_SUCCESS } from './auth'
import { showBanner } from './banners'
import { addLibrariesToStructure, addLibrariesToQuery } from '../utils/images'
import { pluralize } from '../utils/string'

const QUERY_SELECT = 'blayzr/query/QUERY_SELECT'
const QUERY_REMOVE = 'blayzr/query/QUERY_REMOVE'
const QUERY_ADD_TERM = 'blayzr/query/ADD_TERM'
const QUERY_REMOVE_TERM = 'blayzr/query/REMOVE_TERM'
const QUERY_EDIT_TERM = 'blayzr/query/EDIT_TERM'
const QUERY_UPDATE_TERM = 'blayzr/query/UPDATE_TERM'
const QUERY_UPDATE_OPERATOR = 'blayzr/query/UPDATE_OPERATOR'
const QUERY_UPDATE_SORT_ORDER = 'blayzr/query/UPDATE_SORT_ORDER'
const QUERY_UPDATE_RATING = 'blayzr/query/UPDATE_RATING'
const QUERY_REMOVE_ALL_CAMERAS = 'blayzr/query/REMOVE_ALL_CAMERAS'
const QUERY_RESET_LIBRARIES = 'blayzr/query/RESET_LIBRARIES'
const QUERY_SET_ROOT_PROPERTY = 'blayzr/query/SET_ROOT_PROPERTY'
const QUERY_UPDATE_TARGET_DATE = 'blayzr/query/UPDATE_TARGET_DATE'
const QUERY_CLEAR_EDITING_STATE = 'blayzr/query/CLEAR_EDITING_STATE'
const REPLACE_STRUCTURE = 'blayzr/query/REPLACE_STRUCTURE'

const SET_IMAGE_SELECT_MODE = 'blayzr/query/SET_IMAGE_SELECT_MODE'
const SET_IMAGE_REORDER_MODE = 'blayzr/query/SET_IMAGE_REORDER_MODE'
const SELECT_IMAGE = 'blayzr/query/SELECT_IMAGE'
const DESELECT_IMAGE = 'blayzr/query/DESELECT_IMAGE'
const EXCLUDE_IMAGES = 'blayzr/query/EXCLUDE_IMAGES'
const UNEXCLUDE_ALL_IMAGES = 'blayzr/query/UNEXCLUDE_ALL_IMAGES'
const UNINCLUDE_ALL_IMAGES = 'blayzr/query/UNINCLUDE_ALL_IMAGES'
const OPEN_ADVANCED_SEARCH = 'blayzr/query/OPEN_ADVANCED_SEARCH'
const UPDATE_THUMB_SIZE = 'blayzr/query/UPDATE_THUMB_SIZE'
const FIX_ALBUM = 'blayzr/query/FIX_ALBUM'
const UNFIX_ALBUM = 'blayzr/query/UNFIX_ALBUM'

export const DEFAULT_OPERATOR = 'must'
export const DEFAULT_QUERY_OPERATOR = 'should'

export const emptyQuery = {
  operator: DEFAULT_QUERY_OPERATOR,
  freetext: [],
  keyword: [],
  tags: [],
  camera: [],
  rating: [],
  date: [],
  location: [],
  other: [],
  library: [],
  album: [],
  path: [],
  dynlink: []
}

export const emptyStructure = {
  queries: [ emptyQuery ],
  include: [],
  exclude: [],
  sort: {
    value: 'relevance',
    operator: null,
    manual: null
  }
}

const initialState = {
  selectedIndex: 0,
  structure: emptyStructure,
  libraries: [],
  selectImageMode: false,
  reorderImageMode: false,
  selectedImages: [],
  thumbSize: 120,
  editing: {
    type: null,
    index: null
  }
}

export default function reducer (state = initialState, action = {}) {
  const { selectedIndex, structure, libraries } = state
  const { queries } = structure
  const { type, payload, meta } = action
  switch (type) {
    case QUERY_SELECT:
      if (payload < 0) {
        return state
      }
      return {
        ...state,
        selectedIndex: payload,
        structure: {
          ...structure,
          queries: [
            ...queries.slice(0, payload),
            { ...(queries[payload] || addLibrariesToQuery(emptyQuery, libraries)) },
            ...queries.slice(payload + 1)
          ]
        }
      }
    case QUERY_REMOVE:
      if (queries.length === 1) {
        return state
      }
      return {
        ...state,
        selectedIndex: Math.min(selectedIndex, queries.length - 2),
        structure: {
          ...structure,
          queries: [
            ...queries.slice(0, payload),
            ...queries.slice(payload + 1)
          ]
        }
      }
    case QUERY_ADD_TERM:
    case QUERY_REMOVE_TERM:
    case QUERY_EDIT_TERM:
    case QUERY_UPDATE_TERM:
    case QUERY_UPDATE_OPERATOR:
    case QUERY_UPDATE_RATING:
    case QUERY_REMOVE_ALL_CAMERAS:
    case QUERY_RESET_LIBRARIES:
      const newState = {
        ...state,
        structure: {
          ...structure,
          queries: [
            ...queries.slice(0, selectedIndex),
            queryReducer(queries[selectedIndex], action, state.libraries, state.editing),
            ...queries.slice(selectedIndex + 1)
          ]
        }
      }
      if (type === QUERY_EDIT_TERM && meta.type === 'dynlink') {
        newState.editing = {
          type: meta.type,
          index: meta.termIndex
        }
      }
      if (type === QUERY_ADD_TERM || type === QUERY_UPDATE_TERM) {
        // Reset editing state
        newState.editing = initialState.editing
      }
      return newState
    case QUERY_CLEAR_EDITING_STATE:
      return {
        ...state,
        editing: initialState.editing
      }
    case QUERY_SET_ROOT_PROPERTY:
      return {
        ...state,
        structure: {
          ...structure,
          [meta.key]: payload
        }
      }
    case QUERY_UPDATE_TARGET_DATE:
      return {
        ...state,
        structure: {
          ...structure,
          date_target: payload
        }
      }
    case REPLACE_STRUCTURE:
      return {
        ...state,
        selectedIndex: 0,
        structure: mergeWith({}, addLibrariesToStructure(emptyStructure, libraries), payload, structureMerger),
        selectImageMode: false,
        reorderImageMode: false,
        selectedImages: []
      }
    case LOGIN_SUCCESS:
      const libs = payload.user.libraries.map(library => library.library.library_token)
      return {
        ...state,
        libraries: libs,
        structure: addLibrariesToStructure(structure, libs)
      }
    case SET_IMAGE_SELECT_MODE:
      return {
        ...state,
        selectImageMode: payload,
        selectedImages: [],
        reorderImageMode: false
      }
    case SET_IMAGE_REORDER_MODE:
      return {
        ...state,
        reorderImageMode: payload
      }
    case SELECT_IMAGE:
      return {
        ...state,
        selectImageMode: true,
        selectedImages: uniq(state.selectedImages.concat(payload))
      }
    case DESELECT_IMAGE:
      const selectedImages = state.selectedImages.filter(id => id !== payload)
      return {
        ...state,
        selectedImages,
        selectImageMode: selectedImages.length !== 0
      }
    case EXCLUDE_IMAGES:
      return {
        ...state,
        structure: {
          ...structure,
          exclude: structure.exclude.concat(payload)
        },
        selectImageMode: false,
        reorderImageMode: false,
        selectedImages: []
      }
    case UNEXCLUDE_ALL_IMAGES:
      return {
        ...state,
        structure: {
          ...structure,
          exclude: []
        }
      }
    case UNINCLUDE_ALL_IMAGES:
      return {
        ...state,
        structure: {
          ...structure,
          include: []
        }
      }
    case QUERY_UPDATE_SORT_ORDER:
      return {
        ...state,
        structure: {
          ...structure,
          sort: {
            value: payload,
            operator: meta.operator,
            manual: meta.manual ? [
              ...meta.manual,
              ...(structure.sort.manual || []).slice(meta.manual.length)
            ] : structure.sort.manual
          }
        },
        selectImageMode: false,
        reorderImageMode: false,
        selectedImages: []
      }
    case LOCATION_CHANGE:
      return {
        ...state,
        selectImageMode: false,
        reorderImageMode: false,
        selectedImages: []
      }
    case UPDATE_THUMB_SIZE:
      return {
        ...state,
        thumbSize: payload
      }
    case FIX_ALBUM:
      return {
        ...state,
        structure: {
          ...state.structure,
          fixed: payload
        }
      }
    case UNFIX_ALBUM:
      return {
        ...state,
        structure: {
          ...state.structure,
          fixed: undefined
        }
      }
    default:
      return state
  }
}

function structureMerger (obj, src, key) {
  // Do not do anything funky when merging libraries
  if (key === 'library' && src.length !== 0) {
    return src
  }
  return undefined
}

function queryReducer (state = emptyQuery, action = {}, userLibraries, editing) {
  const { type, payload, meta } = action
  switch (type) {
    case QUERY_ADD_TERM:
      let array = state[meta.type].concat(payload)
      if (meta.type === 'library') {
        // Ensure libraries are not repeated
        array = uniqBy(array, 'value')
      }
      return {
        ...state,
        [meta.type]: array
      }
    case QUERY_REMOVE_TERM:
    case QUERY_EDIT_TERM:
      if (type === QUERY_EDIT_TERM && ['rating', 'dynlink'].indexOf(meta.type) !== -1) {
        // Do not remove rating or dynlinks when editing
        return state
      }
      return {
        ...state,
        [meta.type]: [
          ...state[meta.type].slice(0, meta.termIndex),
          ...state[meta.type].slice(meta.termIndex + 1)
        ]
      }
    case QUERY_UPDATE_TERM:
      // If in editing mode, update at editing index
      let index = editing.type === meta.type ? editing.index : meta.termIndex
      return {
        ...state,
        [meta.type]: [
          ...state[meta.type].slice(0, index),
          payload,
          ...state[meta.type].slice(index + 1)
        ]
      }
    case QUERY_UPDATE_OPERATOR:
      return {
        ...state,
        operator: payload
      }
    case QUERY_UPDATE_RATING:
      return {
        ...state,
        rating: payload
      }
    case QUERY_REMOVE_ALL_CAMERAS:
      return {
        ...state,
        camera: []
      }
    case QUERY_RESET_LIBRARIES:
      return {
        ...state,
        library: userLibraries.map(value => ({ value, operator: 'should' }))
      }
    default:
      return state
  }
}

export function selectQuery (index) {
  return {
    type: QUERY_SELECT,
    payload: index
  }
}

export function removeQuery (index) {
  return {
    type: QUERY_REMOVE,
    payload: index
  }
}

export function addTerm (type, value, operator = DEFAULT_OPERATOR) {
  return {
    type: QUERY_ADD_TERM,
    payload: { value, operator },
    meta: { type }
  }
}

export function removeTerm (type, termIndex = 0) {
  return {
    type: QUERY_REMOVE_TERM,
    meta: { type, termIndex }
  }
}

export function editTerm (type, termIndex = 0, value) {
  return {
    type: QUERY_EDIT_TERM,
    payload: value,
    meta: { type, termIndex }
  }
}

export function updateTerm ({ type, value, termIndex = 0 }) {
  return {
    type: QUERY_UPDATE_TERM,
    payload: value,
    meta: { type, termIndex }
  }
}

export function updateOperator (value) {
  return {
    type: QUERY_UPDATE_OPERATOR,
    payload: value
  }
}

export function updateRating (value) {
  return {
    type: QUERY_UPDATE_RATING,
    payload: value
  }
}

export function updateSortOrder (value, operator = null, manual = null) {
  return {
    type: QUERY_UPDATE_SORT_ORDER,
    payload: value,
    meta: {
      operator,
      manual
    }
  }
}

export function updateManualOrder (array) {
  return updateSortOrder('manual', null, array)
}

export function setRootProperty (key, value) {
  return {
    type: QUERY_SET_ROOT_PROPERTY,
    payload: value,
    meta: { key }
  }
}

export function updateTargetDate (value) {
  return setRootProperty('date_target', value)
}

export function replaceStructure (payload) {
  return {
    type: REPLACE_STRUCTURE,
    payload
  }
}

export function enableImageSelect () {
  return {
    type: SET_IMAGE_SELECT_MODE,
    payload: true
  }
}

export function disableImageSelect () {
  return {
    type: SET_IMAGE_SELECT_MODE,
    payload: false
  }
}

export function enableImageReorder () {
  return {
    type: SET_IMAGE_REORDER_MODE,
    payload: true
  }
}

export function selectImage (payload) {
  return {
    type: SELECT_IMAGE,
    payload
  }
}

export function deselectImage (payload) {
  return {
    type: DESELECT_IMAGE,
    payload
  }
}

export function excludeImages (payload) {
  return dispatch => {
    dispatch({
      type: EXCLUDE_IMAGES,
      payload
    })
    dispatch(showBanner(`${pluralize(payload.length, 'image')} removed from album`))
  }
}

export function unexcludeAllImages () {
  return {
    type: UNEXCLUDE_ALL_IMAGES
  }
}

export function unincludeAllImages () {
  return {
    type: UNINCLUDE_ALL_IMAGES
  }
}

export function removeAllCameras () {
  return {
    type: QUERY_REMOVE_ALL_CAMERAS
  }
}

export function resetLibraries () {
  return {
    type: QUERY_RESET_LIBRARIES
  }
}

export function clearEditingState () {
  return {
    type: QUERY_CLEAR_EDITING_STATE
  }
}

export function openAdvancedSearch (type) {
  return {
    type: OPEN_ADVANCED_SEARCH,
    meta: { type }
  }
}

export function updateThumbSize (size) {
  return {
    type: UPDATE_THUMB_SIZE,
    payload: size
  }
}

export function fixAlbum (images) {
  return {
    type: FIX_ALBUM,
    payload: images
  }
}

export function unfixAlbum () {
  return {
    type: UNFIX_ALBUM
  }
}

export const queryFormPlugin = {
  query: (state = {}, action = {}) => {
    switch (action.type) {
      case QUERY_ADD_TERM:
      case QUERY_UPDATE_TERM:
      case QUERY_SET_ROOT_PROPERTY:
      case QUERY_SELECT:
        if (action.meta && ['library', 'camera', 'rating'].indexOf(action.meta.type) !== -1) {
          return state
        }
        if (action.type === QUERY_SET_ROOT_PROPERTY && action.meta.key === 'diversity') {
          return state
        }
        return {
          ...state,
          values: {
            ...state.values,
            search: {}
          }
        }
      case QUERY_EDIT_TERM:
        return {
          ...state,
          values: editReducer(state.values, action)
        }
      case OPEN_ADVANCED_SEARCH:
        return {
          ...state,
          values: {
            ...state.values,
            search: {
              ...(state.values ? state.values.search : null),
              dropdown: true,
              option: action.meta.type
            }
          }
        }
      case QUERY_UPDATE_RATING:
        // Clear ratings stored by redux-form when removing all ratings
        return {
          ...state,
          values: {
            ...state.values,
            rating: action.payload.length === 0 ? [] : state.values.rating
          }
        }
      default:
        return state
    }
  }
}

function editReducer (state = {}, action = {}) {
  switch (action.meta.type) {
    case 'freetext':
    case 'keyword':
      return {
        ...state,
        search: {
          text: action.payload.value,
          operator: action.payload.operator
        }
      }
    case 'rating':
    case 'location':
    case 'camera':
    case 'date':
    case 'other':
    case 'dynlink':
      return {
        ...state,
        search: {
          dropdown: true,
          option: action.meta.type
        },
        [action.meta.type]: action.payload
      }
    default:
      return state
  }
}



// WEBPACK FOOTER //
// ./src/redux/query.js